import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { sizeRange } from '../api/size-set-mappers';
import {
  sizeRangeSchema,
  type SizeRangeInput,
  type SizeRangeValues,
  type SizeRowInput,
} from '../schemas/size-set-form-schema';

export interface SizeSetRangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Values already in the grid; the range skips them. */
  existing: readonly string[];
  onAdd: (rows: SizeRowInput[]) => void;
}

const FIELDS = ['from', 'to', 'step'] as const;
const PLACEHOLDERS = { from: '3', to: '13', step: '0.25' } as const;

function RangeForm({
  existing,
  onAdd,
  onClose,
}: Omit<SizeSetRangeDialogProps, 'open' | 'onOpenChange'> & { onClose: () => void }) {
  const { t } = useTranslation(['sizeSets', 'common']);
  const form = useForm<SizeRangeInput, unknown, SizeRangeValues>({
    resolver: useI18nResolver(sizeRangeSchema),
    defaultValues: { from: '', to: '', step: '1' },
  });
  const draft = useWatch({ control: form.control });
  const parsed = sizeRangeSchema.safeParse(draft);
  const taken = new Set(existing);
  const preview = parsed.success ? sizeRange(parsed.data) : [];
  const fresh = preview.filter((row) => !taken.has(row.value));

  const submit = form.handleSubmit((values) => {
    onAdd(sizeRange(values).filter((row) => !taken.has(row.value)));
    onClose();
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-3">
          {FIELDS.map((name) => (
            <FormField
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t(`sizeSets:range.${name}`)}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoFocus={name === 'from'}
                      inputMode="decimal"
                      placeholder={PLACEHOLDERS[name]}
                      className="tabular-nums"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
        </div>
        {parsed.success ? (
          <p className="text-muted-foreground text-sm" aria-live="polite">
            {t('sizeSets:range.preview', { count: fresh.length })}
            {preview.length > fresh.length
              ? ` ${t('sizeSets:range.skipped', { count: preview.length - fresh.length })}`
              : null}
          </p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit">{t('sizeSets:range.submit')}</Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/** "Add range": appends sizes from, to, by step, such as ring sizes 3 to 13 by 0.25. */
export function SizeSetRangeDialog({
  open,
  onOpenChange,
  existing,
  onAdd,
}: SizeSetRangeDialogProps) {
  const { t } = useTranslation('sizeSets');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('range.title')}</DialogTitle>
          <DialogDescription>{t('range.description')}</DialogDescription>
        </DialogHeader>
        {open ? (
          <RangeForm existing={existing} onAdd={onAdd} onClose={() => onOpenChange(false)} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
